import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import ScenariosPage from './pages/ScenariosPage';
import ScenarioEditorPage from './pages/ScenarioEditorPage';
import FleetEditorPage from './pages/FleetEditorPage';
import ScenarioRedirect from './pages/ScenarioRedirect';
import ComparePage from './pages/ComparePage';
import ScenarioComparePage from './pages/ScenarioComparePage';
import LoginPage from './pages/LoginPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import MethodologyPage from './pages/MethodologyPage';
import WorksCitedPage from './pages/WorksCitedPage';
import AccountPage from './pages/AccountPage';
import AdminPage from './pages/AdminPage';
import WelcomePage, { WELCOME_COMPLETED_KEY } from './pages/WelcomePage';
import { useAuthStore } from './store/useAuthStore';
import { useAppModeStore } from './store/useAppModeStore';

/**
 * Phase 25.6 — has the user engaged enough with the app that we can skip the
 * /welcome landing page? Welcome submit sets the localStorage flag; pre-25.6
 * users who never see /welcome are auto-seeded inside useAppModeStore.initialize
 * (any tour completed or app_mode=fleet implies a user who picked a side).
 */
function isWelcomeCompleted(): boolean {
  try {
    return localStorage.getItem(WELCOME_COMPLETED_KEY) === 'true';
  } catch {
    return true;
  }
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, initialized } = useAuthStore();

  if (!initialized) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          background: '#0a0a0f',
          color: '#a0aec0',
        }}
      >
        Loading…
      </div>
    );
  }

  if (!user) {
    // Phase 25.6: unauthenticated users now land on /welcome (with a
    // "Sign in" link) rather than being bounced straight to /login.
    return <Navigate to="/welcome" replace />;
  }

  return <>{children}</>;
}

/**
 * Phase 25.6 — root route gating. Authenticated users who have never
 * completed the welcome flow get sent there once; everyone else hits the
 * scenarios list (current behavior). Unauthenticated users are caught by
 * ProtectedRoute above and bounced to /welcome.
 */
function RootRedirect() {
  if (!isWelcomeCompleted()) {
    return <Navigate to="/welcome" replace />;
  }
  return <ScenariosPage />;
}

export default function App() {
  const { initialize, user, initialized } = useAuthStore();
  const initializeAppMode = useAppModeStore((s) => s.initialize);

  useEffect(() => {
    initialize();
  }, []);

  // Phase 24: load the per-account app_mode once auth has resolved. We re-run
  // when `user` flips so that signing in pulls the persisted mode and signing
  // out falls back to the localStorage default.
  useEffect(() => {
    if (!initialized) return;
    initializeAppMode(!!user);
  }, [initialized, user, initializeAppMode]);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route path="/methodology" element={<MethodologyPage />} />
        {/* Phase 26.6: public Works Cited reference page (no auth). */}
        <Route path="/works-cited" element={<WorksCitedPage />} />
        <Route path="/welcome" element={<WelcomePage />} />
        <Route
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route path="/" element={<RootRedirect />} />
          {/* Phase 22.4: hard mode separation */}
          <Route path="/scenario/individual/new" element={<ScenarioEditorPage />} />
          <Route path="/scenario/individual/:id" element={<ScenarioEditorPage />} />
          <Route path="/scenario/fleet/new" element={<FleetEditorPage />} />
          <Route path="/scenario/fleet/:id" element={<FleetEditorPage />} />
          {/* Legacy mode-blind URLs: redirect based on scenario.mode */}
          <Route path="/scenario/new" element={<Navigate to="/" replace />} />
          <Route path="/scenario/:id" element={<ScenarioRedirect />} />
          <Route path="/compare" element={<ComparePage />} />
          <Route path="/compare-scenarios" element={<ScenarioComparePage />} />
          {/* Phase 26 Part 2: subscription tier + promo redemption surface. */}
          <Route path="/account" element={<AccountPage />} />
          {/* Phase 26.2: admin promo code management — gated client- and
              server-side on the project owner's user id; non-admins see 404. */}
          <Route path="/admin" element={<AdminPage />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
